/* Sizes share the product's gallery; selection must not reorder it or scroll the page. */
if (!customElements.get('lw-size-guide')) {
  let guideNumber = 0;
  customElements.define('lw-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.guideController?.abort();
      this.guideObserver?.disconnect();
      this.guideController = new AbortController();
      const signal = this.guideController.signal;
      const initialize = () => {
        if (!this.isConnected || this.guideReady) return;
        const tabs = [...this.querySelectorAll('[data-guide-tab]')];
        const panels = [...this.querySelectorAll('[data-guide-panel]')];
        if (tabs.length !== 2 || panels.length !== 2) return;
        this.guideReady = true;
        const prefix = `lw-size-guide-${++guideNumber}`;
        let selected = 0;
        const render = () => {
          const wide = this.clientWidth >= 650;
          this.querySelector('.lwsg-tabs').hidden = wide;
          tabs.forEach((tab, index) => {
            tab.id = `${prefix}-tab-${index}`;
            tab.setAttribute('aria-controls', `${prefix}-panel-${index}`);
            tab.setAttribute('aria-selected', String(index === selected));
            tab.tabIndex = index === selected ? 0 : -1;
            panels[index].id = `${prefix}-panel-${index}`;
            panels[index].hidden = !wide && index !== selected;
            if (wide) {
              panels[index].removeAttribute('role');
              panels[index].removeAttribute('aria-labelledby');
            } else {
              panels[index].setAttribute('role', 'tabpanel');
              panels[index].setAttribute('aria-labelledby', tab.id);
            }
          });
        };
        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => { selected = index; render(); }, {signal});
          tab.addEventListener('keydown', event => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            selected = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
            render();
            tabs[selected].focus();
          }, {signal});
        });
        const units = this.querySelector('[data-guide-inches]');
        const updateUnits = () => {
          this.querySelector('[data-guide-unit="cm"]').hidden = units.checked;
          this.querySelector('[data-guide-unit="inches"]').hidden = !units.checked;
        };
        this.querySelector('.lwsg-units').hidden = false;
        units.addEventListener('change', updateUnits, {signal});
        updateUnits();
        this.guideObserver = new ResizeObserver(render);
        this.guideObserver.observe(this);
        render();
      };
      // Initial parsing can connect a custom element before its children exist.
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once:true, signal});
      else queueMicrotask(initialize);
    }
    disconnectedCallback() {
      this.guideController?.abort();
      this.guideObserver?.disconnect();
      this.guideReady = false;
    }
  });
}
if (!customElements.get('lw-grid-media-gallery')) {
  customElements.whenDefined('media-gallery').then(() => {
    if (customElements.get('lw-grid-media-gallery')) return;
    const NativeGallery = customElements.get('media-gallery');
    customElements.define('lw-grid-media-gallery', class extends NativeGallery {
      setActiveMedia() { /* Native price/form updates continue; the size-independent gallery stays put. */ }
      init() {
        super.init();
        if (this.mobileController || !this.elements?.viewer) return;
        this.mobileController = new AbortController();
        const viewer = this.elements.viewer;
        const measure = () => {
          this.mobileFrame = null;
          if (!this.isConnected) return;
          if (!window.matchMedia('(max-width: 767px)').matches) {
            viewer.style.removeProperty('--lw-mobile-height');
            return;
          }
          const center = viewer.getBoundingClientRect().left + viewer.clientWidth / 2;
          const slides = [...this.querySelectorAll('.product__media-item')];
          const active = slides.reduce((best, slide) => {
            const rect = slide.getBoundingClientRect();
            const distance = Math.abs(rect.left + rect.width / 2 - center);
            return !best || distance < best.distance ? {slide, distance} : best;
          }, null);
          const media = active?.slide.querySelector('.product__media-container');
          if (media) {
            const scrollbar = viewer.offsetHeight - viewer.clientHeight;
            viewer.style.setProperty('--lw-mobile-height', Math.ceil(media.getBoundingClientRect().height + scrollbar) + 'px');
          }
        };
        const schedule = () => {
          if (this.mobileFrame == null) this.mobileFrame = requestAnimationFrame(measure);
        };
        viewer.addEventListener('scroll', schedule, {passive:true, signal:this.mobileController.signal});
        window.addEventListener('resize', schedule, {passive:true, signal:this.mobileController.signal});
        this.mobileObserver = new ResizeObserver(schedule);
        this.querySelectorAll('.product__media-container').forEach(media => this.mobileObserver.observe(media));
        schedule();
      }
      disconnectedCallback() {
        this.mobileController?.abort();
        this.mobileController = null;
        this.mobileObserver?.disconnect();
        if (this.mobileFrame != null) cancelAnimationFrame(this.mobileFrame);
        this.mobileFrame = null;
        if (super.disconnectedCallback) super.disconnectedCallback();
      }
      updateMediaLayout() {
        // Mobile uses the browser's touch scrolling, not Swiper transforms.
        if (window.matchMedia('(max-width: 767px)').matches) {
          this.destroySlider();
          if (this.thumbsInstance && this.thumbsInstance.slider) {
            this.thumbsInstance.slider.destroy();
            this.thumbsInstance = false;
          }
          return;
        }
        if (this.elements?.viewer) this.elements.viewer.scrollLeft = 0;
        super.updateMediaLayout();
      }
    });
  });
}
if (!customElements.get('lw-grid-product-layout')) {
  customElements.define('lw-grid-product-layout', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const initialize = () => {
        if (!this.isConnected) return;
        const panel = this.querySelector('.lw-grid-details');
        if (!panel) return;
        const header = document.getElementById('SiteHeader');
        const measure = () => {
          this.frame = null;
          if (!this.isConnected) return;
          const rect = header?.getBoundingClientRect();
          const bottom = rect && rect.top <= 1 && rect.bottom > 0 ? rect.bottom : 0;
          const top = Math.ceil(Math.max(Number(this.dataset.offset) || 0, bottom + (bottom ? 16 : 0)));
          this.style.setProperty('--lw-sticky-top', top + 'px');
          panel.classList.toggle('lw-grid-fits', panel.getBoundingClientRect().height <= window.innerHeight - top - 24);
        };
        const schedule = () => { if (this.frame == null) this.frame = requestAnimationFrame(measure); };
        this.observer = new ResizeObserver(schedule);
        this.observer.observe(panel);
        if (header) this.observer.observe(header);
        window.addEventListener('resize', schedule, {signal, passive:true});
        window.addEventListener('scroll', schedule, {signal, passive:true});
        measure();
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once:true,signal});
      else initialize();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
}


/* Calendar days in India, independent of the visitor's time zone. */
if (!customElements.get('lw-delivery-estimate')) {
  customElements.define('lw-delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      this.updateDelivery = () => {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Kolkata', year: 'numeric', month: 'numeric', day: 'numeric'
        }).formatToParts(new Date());
        const part = name => Number(parts.find(p => p.type === name).value);
        const day = Date.UTC(part('year'), part('month') - 1, part('day'));
        const format = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'UTC', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'
        });
        const label = this.querySelector('[data-delivery-dates]');
        if (label) label.textContent = `${format.format(new Date(day + 10 * 86400000))} – ${format.format(new Date(day + 11 * 86400000))}`;
      };
      this.updateDelivery();
      // Also handles elements connected while the HTML is still being parsed.
      queueMicrotask(this.updateDelivery);
      clearInterval(this.deliveryTimer);
      this.deliveryTimer = setInterval(this.updateDelivery, 30000);
    }
    disconnectedCallback() {
      clearInterval(this.deliveryTimer);
    }
  });
}

/* Inline chart modal: no iframe or navigation. */
if (!customElements.get('lw-size-chart-popup')) {
  customElements.define('lw-size-chart-popup', class extends HTMLElement {
    connectedCallback() {
      this.chartController?.abort();
      this.chartController = new AbortController();
      const signal = this.chartController.signal;
      const init = () => {
        if (!this.isConnected || signal.aborted) return;
        const trigger = this.querySelector('[data-chart-open]');
        const dialog = this.querySelector('dialog');
        const close = this.querySelector('[data-chart-close]');
        if (!trigger || !dialog || !close) return;
        trigger.addEventListener('click', () => {
          if (!dialog.open) dialog.showModal();
        }, {signal});
        close.addEventListener('click', () => dialog.close(), {signal});
        dialog.addEventListener('click', event => {
          const r = dialog.getBoundingClientRect();
          if (event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) dialog.close();
        }, {signal});
        dialog.addEventListener('close', () => trigger.focus(), {signal});

      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true, signal});
      else queueMicrotask(init);
    }
    disconnectedCallback() { this.chartController?.abort(); }
  });
}

/* Instance-scoped controls for the original chart, including section reloads. */
if (!customElements.get('lw-inline-size-guide')) {
  let guideCount = 0;
  customElements.define('lw-inline-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.inlineController?.abort();
      this.inlineObserver?.disconnect();
      this.inlineController = new AbortController();
      const signal = this.inlineController.signal;
      const initialize = () => {
        if (!this.isConnected || signal.aborted) return;
        const units = [...this.querySelectorAll('.lwsg-unit-btn')];
        const unitPanels = [...this.querySelectorAll('.lwsg-unit-panel')];
        const tabs = [...this.querySelectorAll('.lwsg-tab-btn')];
        const panels = [...this.querySelectorAll('.lwsg-panel')];
        const navigation = this.querySelector('.lwsg-mobile-nav');
        if (tabs.length !== 2 || panels.length !== 2 || !navigation) return;
        const prefix = `lw-inline-guide-${++guideCount}`;
        let selected = 0;
        tabs.forEach((tab, index) => {
          tab.id = `${prefix}-tab-${index}`;
          panels[index].id = `${prefix}-panel-${index}`;
          tab.setAttribute('aria-controls', panels[index].id);
        });
        const render = () => {
          const wide = this.clientWidth >= 860;
          navigation.hidden = wide;
          tabs.forEach((tab, index) => {
            const active = selected === index;
            tab.classList.toggle('is-active', active);
            tab.setAttribute('aria-selected', String(active));
            tab.tabIndex = active ? 0 : -1;
            panels[index].hidden = !wide && !active;
            if (wide) {
              panels[index].removeAttribute('role');
              panels[index].removeAttribute('aria-labelledby');
            } else {
              panels[index].setAttribute('role', 'tabpanel');
              panels[index].setAttribute('aria-labelledby', tab.id);
            }
          });
        };
        units.forEach(button => button.addEventListener('click', () => {
          units.forEach(unit => {
            const active = unit === button;
            unit.classList.toggle('is-active', active);
            unit.setAttribute('aria-pressed', String(active));
          });
          unitPanels.forEach(panel => {
            const active = panel.dataset.unitContent === button.dataset.unit;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
        }, {signal}));
        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => { selected = index; render(); }, {signal});
          tab.addEventListener('keydown', event => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            selected = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
            render();
            tabs[selected].focus();
          }, {signal});
        });
        render();
        this.inlineObserver = new ResizeObserver(render);
        this.inlineObserver.observe(this);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once:true, signal});
      else queueMicrotask(initialize);
    }
    disconnectedCallback() {
      this.inlineController?.abort();
      this.inlineObserver?.disconnect();
    }
  });
}
